import { forwardRef, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type DragEvent, type FormHTMLAttributes, type HTMLAttributes, type TextareaHTMLAttributes } from "react";
import { ArrowUp, FileText, Loader2, Paperclip, Square, X, XCircle } from "lucide-react";
import { cn } from "../../lib/cn";
import { microcopy, useDocumentLocale } from "../../lib/microcopy";
import { Tooltip } from "../overlays";

export interface PromptInputProps extends Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit"> {
  onSubmit: () => void;
  /** Takes files dropped on the composer or pasted into it; without it the composer ignores files. */
  onFiles?: (files: File[]) => void;
}

const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/** The composer card: a growing textarea above a toolbar, with attachments shown inside it and files accepted by drag and drop or paste. */
export function PromptInput({ onSubmit, onFiles, className, children, ...props }: PromptInputProps) {
  const locale = useDocumentLocale();
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      onDragEnter={onFiles ? (e) => { if (!carriesFiles(e)) return; e.preventDefault(); depth.current += 1; setDragging(true); } : undefined}
      onDragOver={onFiles ? (e) => { if (carriesFiles(e)) e.preventDefault(); } : undefined}
      onDragLeave={onFiles ? () => { depth.current = Math.max(0, depth.current - 1); if (depth.current === 0) setDragging(false); } : undefined}
      onDrop={onFiles ? (e) => {
        if (!carriesFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setDragging(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) onFiles(files);
      } : undefined}
      onPaste={onFiles ? (e) => {
        const files = Array.from(e.clipboardData?.files ?? []);
        if (files.length) { e.preventDefault(); onFiles(files); }
      } : undefined}
      className={cn(
        "relative flex w-full flex-col rounded-2xl border border-sand-300 bg-white shadow-sand transition-[border-color,box-shadow] focus-within:border-aegold-400 focus-within:shadow-[0_0_0_4px_rgb(182_138_53/0.12)]",
        className,
      )}
      {...props}
    >
      {children}
      {dragging ? (
        <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-2xl border-2 border-dashed border-aegold-400 bg-aegold-50/95 text-sm font-medium text-aegold-800">
          <span className="inline-flex items-center gap-2"><Paperclip className="size-4" /> {microcopy("dropFiles", locale)}</span>
        </div>
      ) : null}
    </form>
  );
}

export interface PromptInputTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Enter sends (Shift+Enter starts a new line, and nothing is sent while an input method is composing). */
  onSubmitKey?: () => void;
  maxHeight?: number;
}

export const PromptInputTextarea = forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(({ className, onSubmitKey, onKeyDown, maxHeight = 220, value, ...props }, ref) => {
  const inner = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [value, maxHeight]);
  return (
    <textarea
      ref={(el) => {
        inner.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
      }}
      rows={1}
      dir="auto"
      value={value}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          onSubmitKey?.();
        }
      }}
      className={cn("block w-full resize-none bg-transparent px-4 pb-1 pt-3.5 text-sm leading-6 text-aeblack-900 outline-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-sand-500 disabled:opacity-60", className)}
      {...props}
    />
  );
});
PromptInputTextarea.displayName = "PromptInputTextarea";

export function PromptInputToolbar({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-center gap-1 px-2 pb-2 pt-1", className)} {...props} />;
}

export function PromptInputTools({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex min-w-0 flex-1 flex-wrap items-center gap-0.5", className)} {...props} />;
}

export const PromptInputButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean; tooltip?: string }>(
  ({ className, active, tooltip, type = "button", ...props }, ref) => {
    const button = (
      <button
        ref={ref}
        type={type}
        aria-pressed={active}
        className={cn(
          "inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-sand-700 transition-colors hover:bg-sand-100 hover:text-aeblack-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
          active && "bg-aegold-50 text-aegold-800 hover:bg-aegold-100 hover:text-aegold-900",
          className,
        )}
        {...props}
      />
    );
    return tooltip ? <Tooltip content={tooltip}>{button}</Tooltip> : button;
  },
);
PromptInputButton.displayName = "PromptInputButton";

/** Sends the draft; while an answer streams and `onStop` is given, it stops the answer instead. */
export function PromptInputSubmit({ streaming = false, disabled, onStop, label, stopLabel, className }: {
  streaming?: boolean; disabled?: boolean; onStop?: () => void; label?: string; stopLabel?: string; className?: string;
}) {
  const locale = useDocumentLocale();
  if (streaming && onStop) {
    const text = stopLabel ?? microcopy("stop", locale);
    return (
      <Tooltip content={text}>
        <button type="button" onClick={onStop} aria-label={text} className={cn("grid size-9 shrink-0 place-items-center rounded-full bg-aeblack-950 text-white transition hover:bg-aeblack-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}>
          <Square className="size-3.5 fill-current" />
        </button>
      </Tooltip>
    );
  }
  const text = label ?? microcopy("send", locale);
  return (
    <button
      type="submit"
      disabled={disabled}
      aria-label={text}
      title={text}
      className={cn(
        "grid size-9 shrink-0 place-items-center rounded-full bg-gradient-gold text-white shadow-gold transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:bg-none disabled:bg-sand-200 disabled:text-sand-500 disabled:shadow-none",
        className,
      )}
    >
      <ArrowUp className="size-4" />
    </button>
  );
}

export function PromptInputAttachments({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-wrap gap-1.5 px-3 pt-3", className)} {...props} />;
}

export function PromptInputAttachment({ name, status, statusLabel, onRemove, removeLabel, className }: {
  name: string; status?: "uploading" | "uploaded" | "failed"; statusLabel?: string; onRemove?: () => void; removeLabel?: string; className?: string;
}) {
  const locale = useDocumentLocale();
  return (
    <span className={cn("inline-flex max-w-full items-center gap-1.5 rounded-lg border bg-sand-50 py-1 pe-1 ps-2 text-xs", status === "failed" ? "border-danger-200 text-danger-700" : "border-sand-200 text-aeblack-900", className)}>
      {status === "uploading" ? <Loader2 className="size-3.5 animate-spin text-aegold-700" /> : status === "failed" ? <XCircle className="size-3.5" /> : <FileText className="size-3.5 text-aegold-700" />}
      <span className="max-w-48 truncate" dir="auto">{name}</span>
      {statusLabel || status === "uploading" ? <span className="pe-1 text-[11px] text-muted-foreground">{statusLabel ?? microcopy("uploading", locale)}</span> : null}
      {onRemove ? (
        <button type="button" onClick={onRemove} aria-label={removeLabel ?? microcopy("remove", locale)} className="rounded p-0.5 text-sand-600 hover:bg-sand-200 hover:text-aeblack-900">
          <X className="size-3" />
        </button>
      ) : <span className="w-1" />}
    </span>
  );
}
